import React, { useEffect, useRef } from 'react';

const TradingViewWidget = ({ symbol = 'AAPL', interval = '5', onHorizontalLinePrice }) => {
  const container = useRef();
  const widget = useRef(null);

  useEffect(() => {
    // Only create widget if it doesn't exist or symbol/interval changed
    if (!widget.current || widget.current.options.symbol !== symbol || widget.current.options.interval !== interval) {
      // Clean up existing widget
      if (widget.current) {
        try {
          widget.current.remove();
        } catch (e) {
          console.log('Error removing widget:', e);
        }
      }

      // Clear container
      if (container.current) {
        container.current.innerHTML = '';
      }

      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      //script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      script.async = true;
      script.onload = () => {
        if (window.TradingView && container.current) {
          widget.current = new window.TradingView.widget({
            autosize: true,
            symbol: symbol,
            interval: interval,
            timezone: 'Etc/UTC',
            theme: 'dark',
            style: '1',
            locale: 'en',
            toolbar_bg: '#131722',
            enable_publishing: false,
            hide_top_toolbar: true,
            hide_side_toolbar: false,
            allow_symbol_change: true,
            container_id: container.current.id,
            enabled_features: [
              'study_templates',
              'timeframes_toolbar',
              'display_market_status',
              'side_toolbar_in_fullscreen_mode',
              'header_in_fullscreen_mode'
            ],
            disabled_features: ['use_localstorage_for_settings'],
            onChartReady: () => {
              console.log('Chart is ready for symbol:', symbol);
              
              try {
                const chart = widget.current.chart();
                console.log('Chart object:', chart);
                
                // Try multiple approaches to detect drawing events
                
                // Approach 1: Listen for drawing creation events
                if (chart.createMultipointShape) {
                  console.log('Chart supports createMultipointShape');
                }
                
                // Approach 2: Listen for study/shape events
                if (chart.onShapeCreated) {
                  chart.onShapeCreated().subscribe(null, (shapeInfo) => {
                    console.log('Shape created:', shapeInfo);
                    if (shapeInfo.name === 'horizontal_line' || shapeInfo.type === 'horizontal_line') {
                      const price = shapeInfo.points && shapeInfo.points[0] ? shapeInfo.points[0].price : null;
                      console.log('Horizontal line placed at price:', price);
                      if (price && onHorizontalLinePrice) {
                        onHorizontalLinePrice(price);
                      }
                    }
                  });
                }
                
                // Approach 3: Listen for any drawing events
                if (chart.subscribe) {
                  try {
                    chart.subscribe('drawing', (drawingInfo) => {
                      console.log('Drawing event detected:', drawingInfo);
                      if (drawingInfo.name === 'horizontal_line' || drawingInfo.type === 'horizontal_line') {
                        console.log('Horizontal line drawing event:', drawingInfo);
                        const price = drawingInfo.points && drawingInfo.points[0] ? drawingInfo.points[0].price : null;
                        if (price && onHorizontalLinePrice) {
                          onHorizontalLinePrice(price);
                        }
                      }
                    });
                  } catch (e) {
                    console.log('Could not subscribe to drawing events:', e);
                  }
                }
                
                // Approach 4: Monitor chart changes periodically
                let lastShapeCount = 0;
                const monitorShapes = () => {
                  try {
                    if (chart.getAllShapes) {
                      const shapes = chart.getAllShapes();
                      console.log('Current shapes on chart:', shapes);
                      
                      if (shapes.length > lastShapeCount) {
                        const newShapes = shapes.slice(lastShapeCount);
                        newShapes.forEach(shape => {
                          console.log('New shape detected:', shape);
                          if (shape.name === 'horizontal_line' || shape.toolName === 'horizontal_line') {
                            const price = shape.points && shape.points[0] ? shape.points[0].price : null;
                            console.log('New horizontal line at price:', price);
                            if (price && onHorizontalLinePrice) {
                              onHorizontalLinePrice(price);
                            }
                          }
                        });
                      }
                      lastShapeCount = shapes.length;
                    }
                  } catch (e) {
                    console.log('Error monitoring shapes:', e);
                  }
                };
                
                // Monitor shapes every 2 seconds
                const shapeMonitor = setInterval(monitorShapes, 2000);
                
                // Cleanup interval when widget is destroyed
                widget.current._shapeMonitor = shapeMonitor;
                
                // Approach 5: Listen for mouse events on the chart container
                const chartContainer = container.current;
                if (chartContainer) {
                  const handleChartClick = (event) => {
                    console.log('Chart clicked at:', event.clientX, event.clientY);
                    // This is a basic approach - you might need to convert screen coordinates to price
                  };
                  
                  chartContainer.addEventListener('click', handleChartClick);
                  
                  // Store event listener for cleanup
                  widget.current._clickListener = () => {
                    chartContainer.removeEventListener('click', handleChartClick);
                  };
                }

              } catch (error) {
                console.log('Chart API access limited:', error);
                console.log('Available chart methods:', Object.getOwnPropertyNames(chart));
              }
            }
          });
        }
      };

      // Only append script if TradingView is not already loaded
      if (!window.TradingView) {
        document.head.appendChild(script);
      } else {
        script.onload();
      }
    }

    return () => {
      // Don't remove widget on every effect cleanup, only on unmount
    };
  }, [symbol, onHorizontalLinePrice]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (widget.current) {
        try {
          // Clean up shape monitor
          if (widget.current._shapeMonitor) {
            clearInterval(widget.current._shapeMonitor);
          }
          
          // Clean up click listener
          if (widget.current._clickListener) {
            widget.current._clickListener();
          }
          
          widget.current.remove();
        } catch (e) {
          console.log('Error removing widget on unmount:', e);
        }
        widget.current = null;
      }
    };
  }, [symbol, interval]);

  return (
    <div 
      id="tradingview-widget" 
      ref={container} 
      style={{ height: '100%', width: '100%' }}
    />
  );
};

export default TradingViewWidget;
